import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { Button } from "@flat-ui";
import { getProjectApiClient } from "@api/project-api/project-api-utils";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { BetaChip } from "@components/common/beta-chip";
import { WarningTwoToneIcon } from "@components/common/icons";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { ContactSupportLocations } from "@custom-types/types";
import { clearStore } from "@faro-lotv/project-source";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useSnapshotRedirect } from "@hooks/navigation/use-snapshot-redirect";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useCanViewProjectMarkups, useProjectMarkupsPermissionName } from "@hooks/project-markups/use-can-view-project-markups";
import { useFetchProjectMarkups } from "@hooks/project-markups/use-fetch-project-markups";
import { Stack } from "@mui-wrapper";
import { NotFoundPage } from "@pages/not-found-page";
import { ProjectBackgroundTasks } from "@pages/project-details/project-bg-tasks/project-background-tasks";
import { ProjectBackgroundTasksSkeleton } from "@pages/project-details/project-bg-tasks/project-background-tasks-skeleton";
import { ProjectButtons } from "@pages/project-details/project-buttons";
import { ProjectIntegrations } from "@pages/project-details/project-integrations/project-integrations";
import { MarkupsViewControls } from "@pages/project-details/project-markups/markups-toolbar/markups-view-controls";
import { ProjectMarkups } from "@pages/project-details/project-markups/project-markups";
import { ProjectOverview } from "@pages/project-details/project-overview/project-overview";
import { ProjectSettings } from "@pages/project-details/project-settings/project-settings";
import { ProjectSnapshots } from "@pages/project-details/project-snapshots/project-snapshots";
import { ProjectTeam } from "@pages/project-details/project-team/project-team";
import { ProjectTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { appStateSelector } from "@store/app/app-selector";
import { deletedProjectsSelector, fetchingProjectsFlagsSelector, isProjectArchiveSelector, selectedProjectContextSelector, selectedProjectSelector } from "@store/projects/projects-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { resetDeletedProjects, resetSelectedProject } from "@store/projects/projects-slice";
import { fetchProjectContext, fetchProjectDetails, fetchProjectSettings } from "@store/projects/projects-slice-thunk";
import { backgroundTasksFetchingStatusSelector, fetchingStatusByTypeSelector, sdbBackgroundTasksSelector } from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { resetSdbBackgroundTasksState } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { resetSlideNodesState } from "@store/slide-nodes/slide-nodes-slice";
import { fetchTasksByType } from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { contactSupport } from "@utils/email-utils";
import { hasDescribeOnlyPermission } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useRef, useState } from "react";

/**
 * Filters the given tasks to only include VideoMode tasks unless dev mode is enabled
 */
export function filterVideoModeTasks(tasks, isDevModeEnabled) {
  return tasks.filter(task => isDevModeEnabled || task.taskType === "VideoMode");
}

/**
 * Display the details of the selected project
 */
export function ProjectDetails() {
  const {
    projectId,
    projectTabs
  } = useAppParams();
  const companyId = useCompanyId();
  const {
    isRedirecting
  } = useSnapshotRedirect({
    companyId,
    projectId
  });
  const {
    trackEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectedProjectSelector);
  const isProjectArchived = useAppSelector(isProjectArchiveSelector(projectId ?? ""));
  const {
    isFetchingProjects,
    isFetchingProjectContext,
    isFetchingProjectSettings,
    processingProjects
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const deletedProjects = useAppSelector(deletedProjectsSelector);
  const selectedProjectContext = useAppSelector(selectedProjectContextSelector);
  const {
    isDevModeEnabled
  } = useAppSelector(appStateSelector);
  const markupsPermissionName = useProjectMarkupsPermissionName();
  const progressApiClient = useProgressApiClient({
    projectId: projectId ?? ""
  });
  const sdbBackgroundTasks = useAppSelector(sdbBackgroundTasksSelector);

  /** Whether the background tasks have been loaded at least once */
  const [hasLoadedTasksOnce, setHasLoadedTasksOnce] = useState(false);

  /**
   * Use the VideoMode-specific fetching status to avoid race conditions
   * in case ALL background tasks are being fetched at the same time
   */
  const videoModeTasksFetchingStatus = useAppSelector(fetchingStatusByTypeSelector("VideoMode"));
  /**
   * In dev mode, we show ALL task types (not just VideoMode), so we need to
   * check the general fetching status.
   */
  const allTasksFetchingStatus = useAppSelector(backgroundTasksFetchingStatusSelector);
  const {
    canViewCompanyMembers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    canViewAllProjectMembers
  } = useHasUserValidPermissionProjectLevel();
  const canViewMarkups = useCanViewProjectMarkups();
  const hasOnlyDescribePermission = hasDescribeOnlyPermission(selectedProject);

  // Keeps `isLoading` true between `isRedirecting` flipping to false and the fetch effect
  // dispatching, to avoid a brief flicker (skeleton -> empty -> skeleton).
  const hasFetchedRef = useRef(false);

  /**
   * Whether the required data to display the project details page is loading.
   * Includes the snapshot redirect check, so
   * the skeleton shows during the redirect process to avoid a flash of the "Not found" page.
   */
  const isLoading = isRedirecting || !hasFetchedRef.current || isFetchingProjects || isFetchingProjectContext || isFetchingProjectSettings;

  // Fetches the project markups once on the project details page, so they are available
  // on every project tab. Waits until the project details and settings of the current
  // project are loaded, since both are needed to know if the user can view the markups.
  const shouldFetchMarkups = !!projectId && selectedProject?.id === projectId && !isLoading && !hasOnlyDescribePermission && canViewMarkups;
  useFetchProjectMarkups({
    projectId: projectId ?? "",
    shouldFetch: shouldFetchMarkups
  });

  // The company members are needed to show the markups assignees, so they are fetched together
  // with the markups to avoid a loading state when opening the annotations tab.
  // The thunk skips the request if the members have already been fetched.
  useEffect(() => {
    if (shouldFetchMarkups && (canViewAllProjectMembers || canViewCompanyMembers)) {
      void dispatch(fetchCompanyMembers({
        companyId
      }));
    }
  }, [canViewAllProjectMembers, canViewCompanyMembers, companyId, dispatch, shouldFetchMarkups]);

  /**
   * Fetches the required data to display the project details:
   * - project details
   * - project context
   * - project settings
   * - background tasks
   */
  useEffect(() => {
    if (projectId && !isRedirecting) {
      hasFetchedRef.current = true;
      /**
       * Skipped project setting and context requests for describe-only projects since those users have no
       * project-level access beyond basic metadata.
       */
      if (!hasOnlyDescribePermission) {
        const projectApiClient = getProjectApiClient({
          projectId
        });

        // We need to check the project settings for viewers as well
        // to check if the annotation visibility is enabled for viewers or not
        void dispatch(fetchProjectSettings({
          projectApiClient
        }));
        void dispatch(fetchProjectContext({
          projectId
        }));
      }
      void dispatch(fetchProjectDetails({
        companyId,
        projectId
      }));

      // In dev mode, fetch ALL task types so the Activity tab is visible
      // for projects with non-VideoMode activities.
      // In normal mode, only fetch VideoMode tasks.
      void dispatch(fetchTasksByType({
        progressApiClient,
        taskType: isDevModeEnabled ? undefined : "VideoMode"
      }));
    }
  }, [companyId, dispatch, hasOnlyDescribePermission, isDevModeEnabled, isRedirecting, progressApiClient, projectId]);

  // Resets all store state associated to the selected project:
  // - Selected project from the projects slice.
  // - IElements slice since it should only store data of the selected project
  // - Slide nodes slice, filled from the sheets of the annotations opened in the project
  // - Progress API tasks slice since it should only store data of the selected project.
  // It runs on unmount and also when the projectId changes, since the router reuses this
  // component when navigating from one project to another.
  useEffect(() => {
    return () => {
      dispatch(resetSelectedProject());
      dispatch(clearStore());
      dispatch(resetSlideNodesState());
      dispatch(resetSdbBackgroundTasksState());
      dispatch(resetDeletedProjects());
    };
  }, [dispatch, projectId]);

  /** Filter out non-video mode tasks unless dev mode is enabled */
  const filteredSdbBackgroundTasks = filterVideoModeTasks(sdbBackgroundTasks, isDevModeEnabled);

  /**
   * We consider the fetch "completed" when it either succeeded or failed.
   * In dev mode, we show ALL task types (via filterVideoModeTasks), so we check
   * the general fetching status. In normal mode, we use the VideoMode-specific
   * fetching status.
   */
  const relevantFetchingStatus = isDevModeEnabled ? allTasksFetchingStatus : videoModeTasksFetchingStatus;
  const isBackgroundTasksFetchCompleted = relevantFetchingStatus !== "uninitialized" && relevantFetchingStatus !== "pending";

  /**
   * The Activity tab should be hidden when there are no background tasks.
   * We use hasLoadedTasksOnce to prevent "Access denied" errors when users reload
   * the page with the Activity tab open - it shows loading content until
   * the fetch completes.
   */
  const shouldHideActivityTab = isBackgroundTasksFetchCompleted && filteredSdbBackgroundTasks.length === 0;

  // useEffect to set hasLoadedTasksOnce to true once the background tasks have been fetched at least once
  useEffect(() => {
    if (isBackgroundTasksFetchCompleted) {
      setHasLoadedTasksOnce(true);
    }
  }, [isBackgroundTasksFetchCompleted]);

  /**
   * Early exit to the "Not found" page if the requires URL route params are not defined or their value is not valid:
   * - companyId
   * - projectId
   * - projectTabs
   */
  if (!projectId || !projectTabs || !Object.values(ProjectTabs).includes(projectTabs)) {
    return <NotFoundPage />;
  }
  if (projectTabs === ProjectTabs.evaluation) {
    return <EmptyPage title="The feature is no longer supported" subtitle="The feature you are looking for is no longer supported in Sphere XG" icon={WarningTwoToneIcon} button={<Button onClick={() => contactSupport({
      location: ContactSupportLocations.projectEvaluationDeprecatedPage,
      trackEvent
    })}>
            Contact support
          </Button>} />;
  }

  /** Whether the current project is being processed or has been recently deleted */
  const isProcessingOrDeleting = projectId && (processingProjects[projectId] || deletedProjects[projectId]);

  /**
   * Early exit to the "Not found" page if after fetching the required data to show the project details
   * one data element is not defined:
   * - project details
   * - project context (not required for describe-only projects)
   * - project settings
   */
  const isDescribeOnly = hasDescribeOnlyPermission(selectedProject);
  if ((selectedProject === null || !isDescribeOnly && selectedProjectContext === null) && !isLoading && !isProcessingOrDeleting) {
    return <NotFoundPage />;
  }

  /** Shows action buttons for the project */
  const projectButtons = selectedProject ? <ProjectButtons project={selectedProject} isLoading={isLoading} /> : undefined;

  /**
   * Hide the members tab if
   * - selected project is not yet available to check access permission.
   * - the user has no workspace or project permission to view project members
   */
  const shouldHideMembersTab = !selectedProject || !(canViewAllProjectMembers || canViewCompanyMembers);
  return <TabsWithRoutes selectedTab={projectTabs} selectedProject={selectedProject} requiredAttribute={selectedProject} trackingEventPageName={OpenTabEvents.openProjectTab} isLoading={isLoading} tabs={[{
    label: "Overview",
    route: ProjectTabs.overview,
    content: project => <ProjectOverview project={project} isLoading={isLoading} />,
    loadingContent: <ProjectOverview isLoading={true} />,
    actionButtons: projectButtons
  }, {
    label: "Members",
    route: ProjectTabs.members,
    content: project_0 => <ProjectTeam project={project_0} companyId={companyId} isLoading={isLoading} />,
    loadingContent: <ProjectTeam isLoading={true} />,
    actionButtons: projectButtons,
    isHidden: shouldHideMembersTab
  }, {
    label: "Activity",
    route: ProjectTabs.activity,
    content: project_1 => <ProjectBackgroundTasks projectId={project_1.id} />,
    loadingContent: <ProjectBackgroundTasksSkeleton />,
    actionButtons: projectButtons,
    requiredPermissionProjectLevel: isDevModeEnabled ? undefined : RequiredPermissionProjectLevelName.canViewProjectCloudActivity,
    isHidden: shouldHideActivityTab,
    isTabLoading: !hasLoadedTasksOnce
  }, {
    label: "Annotations",
    route: ProjectTabs.annotations,
    content: project_2 => <ProjectMarkups projectId={project_2.id} />,
    loadingContent: <div />,
    actionButtons: selectedProject ? <ProjectButtons project={selectedProject} isLoading={isLoading} tabControl={<MarkupsViewControls placement="header" />} /> : undefined,
    requiredPermissionProjectLevel: markupsPermissionName
  }, {
    label: "Integrations",
    route: ProjectTabs.integrations,
    content: project_3 => <ProjectIntegrations projectId={project_3.id} />,
    loadingContent: <div />,
    actionButtons: projectButtons,
    requiredPermissionProjectLevel: RequiredPermissionProjectLevelName.canViewProjectIntegrations,
    isHidden: isProjectArchived,
    isBetaFeature: true,
    popover: {
      title: <Stack sx={{
        flexDirection: "row",
        gap: "8px"
      }}>
                Integrations
                <BetaChip />
              </Stack>,
      // `Popover` already wraps the description in its own `bodyS` text that
      // inherits the popover's colour, so this is plain text: a `Text` here
      // would nest a second one and pin it to the light-scheme colour, which
      // the always-dark popover cannot read.
      description: "Connect Sphere XG with third-party integrations to sync your annotations from Sphere XG to external tools. This feature is in beta and may evolve based on your feedback.",
      shouldShowFeedbackButton: false
    }
  }, {
    label: "Snapshots",
    route: ProjectTabs.snapshots,
    content: project_4 => <ProjectSnapshots projectId={project_4.id} />,
    loadingContent: null,
    actionButtons: projectButtons
  }, {
    label: "Settings",
    route: ProjectTabs.settings,
    content: project_5 => <ProjectSettings projectId={project_5.id} isLoading={isLoading} />,
    loadingContent: <ProjectSettings isLoading={true} />,
    actionButtons: projectButtons,
    requiredPermissionProjectLevel: RequiredPermissionProjectLevelName.canViewProjectSettings
  }]} />;
}